import { TestData } from "./TestData"
import { ILog, IShip, IShipVesselType, ILogWeather, IShipUserCrewRole, IShipUserLevel } from "../../app/Types"

const d = (new Date()).toISOString();

// Fixed demo time: March 29, 2026 at 12:41 UTC (14:41 local time in UTC+2)
// This ensures consistent demo times instead of using current time
export const DEMO_TIME = new Date(Date.UTC(2026, 2, 29, 12, 41, 0)); // Month is 0-indexed, so 2 = March
export const DEMO_TIMEZONE = 2 * 3600; // UTC+2 in seconds

export function day(date: Date): number {
  return Math.floor((date.getTime() - new Date(Date.UTC(2001, 0, 1)).getTime()) / (60 * 60 * 24 * 1000));
}

// Calculate seconds since midnight for a given date
export function timeOfDay(date: Date): { time: number; timeUtc: number; timezone: number } {
  // Calculate seconds since midnight (UTC)
  const todayMidnightUTC = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
  ));
  const timeUtc = Math.floor((date.valueOf() - todayMidnightUTC.valueOf()) / 1000);
  const timezone = DEMO_TIMEZONE;
  const time = timeUtc + timezone;

  return { time, timeUtc, timezone };
}

// Create a current log entry for live NMEA data display
// You can pass overrides to customize the data for different scenes
export function QuickStartCurrentLog(overrides?: Partial<{
  time: Date;
  sog: number;
  course: number;
  distance: number;
  engineRpms: number[];
  comment: string;
  data: unknown;
  labels: string[];
  dayId: number;
}>): ILog {
  const logTime = overrides?.time || DEMO_TIME;
  const { time, timeUtc, timezone } = timeOfDay(logTime);

  return {
    // id 0 marks a pending/unsaved log — matches the live current log the app
    // sends (Log(id: 0, …)), which always carries a data payload.
    id: 0,
    cruiseId: 1,
    tripId: 1,
    dayId: overrides?.dayId ?? 1,
    accountId: 1,
    shipId: 1,
    createdAt: logTime.toISOString(),
    updatedAt: logTime.toISOString(),
    time: time,
    timeUtc: timeUtc,
    timezone: timezone,
    labels: overrides?.labels || ["current"],
    sog: overrides?.sog ?? 0,
    course: overrides?.course ?? 57,
    distance: overrides?.distance ?? 0,
    data: overrides?.data ? JSON.stringify(overrides.data) : "{}",
    ...(overrides?.engineRpms && { engineRpms: overrides.engineRpms }),
    ...(overrides?.comment !== undefined && { comment: overrides.comment }),
  };
}

// Predefined logs for common demo scenarios
// Each log has a specific purpose in the QuickStart demo flow
const PREDEFINED_LOGS: Record<number, Partial<{
  time: Date;
  sog: number;
  stw: number;
  course: number;
  heading: number;
  distance: number;
  depth: number;
  log: number;
  windDirection: number;
  windSpeed: number;
  pressure: number;
  weather: ILogWeather;
  temperature: number;
  seaState: string;
  comment: string;
  engineRpms: number[];
  data: unknown;
  labels: string[];
}>> = {
  1: {
    // Engine 1 start - 14:54 local time
    time: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)),
    sog: 0,
    course: 57,
    distance: 0,
    comment: "Started engine #1",
    engineRpms: [0, 1200, 0],
    labels: ["auto", "engine"],
    data: {
      autoType: "engineChange",
      engine: "1",
      engineOn: true,
    },
  },
  2: {
    // Engine 2 start - 14:54 local time
    time: new Date(Date.UTC(2026, 2, 29, 12, 54, 0)),
    sog: 0,
    course: 25,
    distance: 0,
    comment: "Started engine #2",
    engineRpms: [0, 1200, 1200],
    labels: ["auto", "engine"],
    data: {
      autoType: "engineChange",
      engine: "2",
      engineOn: true,
    },
  },
  3: {
    // Started - boat in motion - 14:55 local time
    time: new Date(Date.UTC(2026, 2, 29, 12, 55, 0)),
    sog: 5.7,
    course: 186,
    distance: 0,
    comment: "End of pause",
    engineRpms: [0, 1200, 1200],
    labels: ["manual"],
  },
  4: {
    time: new Date(Date.UTC(2026, 2, 29, 13, 0, 0)),
    sog: 5.7,
    course: 186,
    distance: 0.4,
    comment: "Automatic entry after 1:00",
    engineRpms: [0, 1200, 1200],
    labels: ["manual"],
  },
  5: {
    time: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
    sog: 5.7,
    stw: 5.7,
    course: 186,
    heading: 186,
    distance: 0.8,
    log: 25812,
    depth: 35.6,
    comment: "",
    engineRpms: [0, 1200, 1200],
    labels: ["manual"],
  },
  6: {
    time: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
    sog: 5.7,
    course: 186,
    distance: 0.9,
    comment: "Stopped engine #1 after 0:10 (0.9 nm)",
    engineRpms: [0, 0, 1200],
    labels: ["manual"],
  },
  7: {
    time: new Date(Date.UTC(2026, 2, 29, 13, 5, 0)),
    sog: 5.7,
    course: 186,
    distance: 0.9,
    windDirection: 310,
    windSpeed: 15.5,
    pressure: 1013.5,
    temperature: 25,
    comment: "Stopped engine #2 after 0:10 (0.9 nm)",
    engineRpms: [0, 0, 0],
    labels: ["manual"],
  },

  8: {
    time: new Date(Date.UTC(2026, 2, 29, 13, 16, 0)),
    sog: 8.6,
    course: 126,
    distance: 2.3,
    comment: "Change of course of 060°",
    engineRpms: [0, 0, 0],
    labels: ["manual"],
  },

  9: {
    time: new Date(Date.UTC(2026, 2, 29, 13, 25, 0)),
    sog: 7.1,
    course: 199,
    distance: 3.6,
    comment: "Change of course of 073°",
    engineRpms: [0, 0, 0],
    labels: ["manual"],
  },

  10: {
    time: new Date(Date.UTC(2026, 2, 29, 14, 0, 0)),
    sog: 7.1,
    course: 199,
    distance: 7.7,
    comment: "Automatic entry after 1:00",
    engineRpms: [0, 0, 0],
    labels: ["manual"],
  },
  11: {
    time: new Date(Date.UTC(2026, 2, 29, 15, 0, 0)),
    sog: 7.1,
    course: 199,
    distance: 14.8,
    comment: "Automatic entry after 1:00",
    engineRpms: [0, 0, 0],
    labels: ["manual"],
  },
  12: {
    time: new Date(Date.UTC(2026, 2, 29, 16, 0, 0)),
    sog: 7.1,
    course: 199,
    distance: 21.9,
    comment: "Automatic entry after 1:00",
    engineRpms: [0, 0, 0],
    labels: ["manual"],
  },

  13: {
    time: new Date(Date.UTC(2026, 2, 29, 16, 8, 0)),
    sog: 7.1,
    course: 133,
    distance: 22.8,
    comment: "Change of course of 066°",
    engineRpms: [0, 0, 0],
    labels: ["manual"],
  },

  14: {
    time: new Date(Date.UTC(2026, 2, 29, 16, 12, 0)),
    sog: 7.1,
    course: 133,
    distance: 23.4,
    comment: "Genoa down",
    engineRpms: [0, 0, 0],
    labels: ["manual"],
  },

  15: {
    time: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)),
    sog: 7.1,
    course: 133,
    distance: 23.4,
    comment: "Started engine #2",
    engineRpms: [0, 0, 1200],
    labels: ["manual"],
  },

  16: {
    time: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)),
    sog: 7.1,
    course: 133,
    distance: 23.4,
    comment: "Mainsail down",
    engineRpms: [0, 0, 1200],
    labels: ["manual"],
  },

  17: {
    time: new Date(Date.UTC(2026, 2, 29, 16, 13, 0)),
    sog: 7.1,
    course: 133,
    distance: 23.4,
    comment: "Started engine #1",
    engineRpms: [0, 1200, 1200],
    labels: ["manual"],
  },
  18: {
    time: new Date(Date.UTC(2026, 2, 29, 16, 17, 0)),
    sog: 0.0,
    course: 133,
    distance: 23.7,
    comment: "Start of pause",
    engineRpms: [0, 1200, 1200],
    labels: ["manual"],
  },

  19: {
    time: new Date(Date.UTC(2026, 2, 29, 16, 19, 0)),
    sog: 0.0,
    course: 133,
    distance: 23.7,
    comment: "Stopped engine #1 after 0:06 (0.2 nm)",
    engineRpms: [0, 0, 1200],
    labels: ["manual"],
  },

  20: {
    time: new Date(Date.UTC(2026, 2, 29, 16, 19, 0)),
    sog: 0.0,
    course: 133,
    distance: 23.7,
    comment: "Stopped engine #2 after 0:06 (0.2 nm)",
    engineRpms: [0, 0, 0],
    labels: ["manual"],
  },

  21: {
    // Engine 1 start - 14:54 local time
    time: new Date(Date.UTC(2026, 2, 30, 7, 25, 0)),
    sog: 0,
    course: 25,
    distance: 0,
    comment: "Started engine #1",
    engineRpms: [0, 1200, 0],
    labels: ["auto", "engine"],
    data: {
      autoType: "engineChange",
      engine: "1",
      engineOn: true,
    },
  },
  22: {
    // Engine 2 start - 14:54 local time
    time: new Date(Date.UTC(2026, 2, 30, 7, 25, 0)),
    sog: 0,
    course: 25,
    distance: 0,
    comment: "Started engine #2",
    engineRpms: [0, 1200, 1200],
    labels: ["auto", "engine"],
    data: {
      autoType: "engineChange",
      engine: "2",
      engineOn: true,
    },
  },
  23: {
    // Started - boat in motion - 14:55 local time
    time: new Date(Date.UTC(2026, 2, 30, 12, 55, 0)),
    sog: 5.7,
    course: 25,
    distance: 0,
    comment: "End of pause",
    engineRpms: [0, 1200, 1200],
    labels: ["manual"],
  },

};

// Create a saved log entry (with ID) from a current log or custom data
// Use this to create saved logs for building up the timeline across scenes
// If no overrides are provided, will use predefined log data if available
export function QuickStartSavedLog(logId: number, overrides?: Partial<{
  time: Date;
  sog: number;
  stw: number;
  course: number;
  heading: number;
  distance: number;
  depth: number;
  log: number;
  windDirection: number;
  windSpeed: number;
  pressure: number;
  weather: ILogWeather;
  temperature: number;
  seaState: string;
  comment: string;
  engineRpms: number[];
  data: unknown;
  labels: string[];
  dayId: number;
  lat: number;
  lng: number;
}>): ILog {
  // Merge predefined log with overrides
  const predefined = PREDEFINED_LOGS[logId] || {};
  const merged = { ...predefined, ...overrides };

  const logTime = merged.time || DEMO_TIME;
  const { time, timeUtc, timezone } = timeOfDay(logTime);

  return {
    id: logId,
    cruiseId: 1,
    tripId: 1,
    dayId: merged.dayId ?? 1,
    accountId: 1,
    shipId: 1,
    createdAt: logTime.toISOString(),
    updatedAt: logTime.toISOString(),
    time: time,
    timeUtc: timeUtc,
    timezone: timezone,
    labels: merged.labels || ["auto"],
    sog: merged.sog ?? 0,
    course: merged.course ?? 57,
    distance: merged.distance ?? 0,
    ...(merged.stw !== undefined && { stw: merged.stw }),
    ...(merged.heading !== undefined && { heading: merged.heading }),
    ...(merged.log !== undefined && { log: merged.log }),
    ...(merged.depth !== undefined && { depth: merged.depth }),
    ...(merged.windDirection !== undefined && { windDirection: merged.windDirection }),
    ...(merged.windSpeed !== undefined && { windSpeed: merged.windSpeed }),
    ...(merged.pressure !== undefined && { pressure: merged.pressure }),
    ...(merged.weather !== undefined && { weather: merged.weather }),
    ...(merged.temperature !== undefined && { temperature: merged.temperature }),
    ...(merged.seaState !== undefined && { seaState: merged.seaState }),
    ...(merged.comment && { comment: merged.comment }),
    ...(merged.engineRpms && { engineRpms: merged.engineRpms }),
    data: merged.data ? JSON.stringify(merged.data) : "{}",
    ...(merged.lat !== undefined && { lat: merged.lat }),
    ...(merged.lng !== undefined && { lng: merged.lng }),
  };
}

// Helper to create multiple saved logs from an array of log IDs
// Use this for scenes that need to show multiple logs
export function QuickStartSavedLogs(logIds: number[], overrides?: Record<number, Partial<{
  time: Date;
  sog: number;
  stw: number;
  course: number;
  heading: number;
  distance: number;
  depth: number;
  log: number;
  windDirection: number;
  windSpeed: number;
  pressure: number;
  weather: ILogWeather;
  temperature: number;
  seaState: string;
  comment: string;
  engineRpms: number[];
  data: unknown;
  labels: string[];
  dayId: number;
  lat: number;
  lng: number;
}>>): ILog[] {
  return logIds.map((id) => QuickStartSavedLog(id, overrides?.[id]));
}

// Create ship users / crew members for demo scenes
// Returns an array with skipper and crew members
export function QuickStartShipUsers() {
  const timestamp = DEMO_TIME.toISOString();

  return [
    { id: 1, accountId: 1, shipId: 1, userId: 1, createdAt: timestamp, updatedAt: timestamp, active: 1, name: "Lucas Martin", shortname: "Lucas", email: "lucas@example.com", shipUserLevel: IShipUserLevel.primary, shipUserCrewRole: IShipUserCrewRole.skipper, data: "{}" },
    { id: 2, accountId: 1, shipId: 1, userId: 2, createdAt: timestamp, updatedAt: timestamp, active: 1, name: "Thomas Schmidt", shortname: "Tom", email: "tom@example.com", shipUserLevel: IShipUserLevel.editAll, shipUserCrewRole: IShipUserCrewRole.crew, data: "{}" },
    { id: 3, accountId: 1, shipId: 1, userId: 3, createdAt: timestamp, updatedAt: timestamp, active: 1, name: "Lisa Weber", shortname: "Lisa", email: "lisa@example.com", shipUserLevel: IShipUserLevel.editAll, shipUserCrewRole: IShipUserCrewRole.crew, data: "{}" },
    { id: 4, accountId: 1, shipId: 1, userId: 4, createdAt: timestamp, updatedAt: timestamp, active: 1, name: "Emma Morgan", shortname: "Emma", email: "emma@example.com", shipUserLevel: IShipUserLevel.editAll, shipUserCrewRole: IShipUserCrewRole.crew, data: "{}" }];
}

export function QuickStartShip(): TestData {
  const ship = {
    id: 1,
    accountId: 1,
    createdAt: d,
    updatedAt: d,
    engineCount: 2,
    name: "Lipoza",
    vesselType: IShipVesselType.sailing,
    shipData: {
      name: "Lipoza",
      engineCount: 2,
      vesselType: IShipVesselType.sailing,
      mmsi: "211234567",
      callsign: "DF1234",
      engines: [
        { id: "1", number: 1, nmeaNumber: 1, engineShortName: "Starboard", name: "Starboard", type: "inboard", fuelType: "diesel" },
        { id: "2", number: 2, nmeaNumber: 2, engineShortName: "Portside", name: "Portside", type: "inboard", fuelType: "diesel" },
      ],
      sails: [
        { id: "mainsail", sail: "mainsail", sailType: "mast", reefNo: 3 },
        { id: "genoa", sail: "genoa", sailType: "forestay", reefNo: 0 },
      ],
    },
    shipUsers: [
      { userId: 1, level: 100, crewRole: "skipper" },
    ],
  } as unknown as IShip;

  const currentDay = day(DEMO_TIME);
  const { timezone } = timeOfDay(DEMO_TIME);

  const data = {
    account: { id: 1, createdAt: d, updatedAt: d, accountSubscriptionPlanId: 1, name: "Test Account", slug: "test-account", metadata: "{}", subscriptionStatus: 1 },
    cruises: [
      { id: 1, createdAt: d, updatedAt: d, accountId: 1, shipId: 1, title: "", startedAt: undefined, completedAt: undefined, data: "{}" },
    ],

    days: [
      { id: 1, cruiseId: 1, tripId: 1, accountId: 1, shipId: 1, createdAt: d, updatedAt: d, day: currentDay, timezone: timezone, data: "{}" },
    ],

    logs: [

    ],

    notes: [],
    attachments: [],
    session: { id: "deadbeef", authorized: true, accountId: 1, userId: 1 },
    ship: ship,
    ships: [ship],

    shipUsers: [
      { id: 1, accountId: 1, shipId: 1, userId: 1, createdAt: d, updatedAt: d, active: 1, name: "Lucas Martin", shortname: "Lucas", email: "skipper@example.com", shipUserLevel: IShipUserLevel.primary, shipUserCrewRole: IShipUserCrewRole.skipper, data: "{}" },
    ],

    statuses: [],
    tracks: [],

    trips: [
      { id: 1, accountId: 1, shipId: 1, cruiseId: 1, createdAt: d, updatedAt: d, title: "", startedAt: undefined, completedAt: undefined, timezone: timezone, data: "{}" },
    ],

    user: { id: 1, createdAt: d, updatedAt: d, authenticationType: 0, name: "DemoUser", email: "testuser@example.com", shortname: "DemoUser" },
    users: [
      { id: 1, accountId: 1, createdAt: d, updatedAt: d, authenticationType: 0, name: "Lucas Martin", email: "lucas@example.com", shortname: "LucasMarti" },
      { id: 2, accountId: 1, createdAt: d, updatedAt: d, authenticationType: 0, name: "Thomas Schmidt", email: "tom@example.com", shortname: "ThomasSchm" },
      { id: 3, accountId: 1, createdAt: d, updatedAt: d, authenticationType: 0, name: "Lisa Weber", email: "lisa@example.com", shortname: "LisaWeber" },
      { id: 4, accountId: 1, createdAt: d, updatedAt: d, authenticationType: 0, name: "Emma Morgan", email: "emma@example.com", shortname: "EmmaMorgan" },
    ],
    weatherTimeseries: [],
  };

  return data;
}
